import { type ItemDetails, LegacySegmentTypes } from '@1debit/analytics';
import { type IconToken } from '@1debit/icons';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { CircledIcon } from './CircledIcon';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { type AsChildProps, ClickableAnalyticsSlot } from '../../client/ClickableAnalyticsSlot';
import { DEFAULT_SIZE, type IconProps } from '../ActivityIndicator/subcomponents/icon';
import { classNames } from '../utils/classNames';

export type BaseIconButtonDefaultProps = {
	analyticsId: AnalyticsId;
	icon: IconToken;
	ariaLabel: string;
	className?: string;
	itemDetails?: ItemDetails;
	itemValue?: string;
	variant?: 'default' | 'inverse' | 'alwaysDark';
	disabled?: boolean;
};

type BaseIconButtonOwnProps = {
	padding: string;
	iconSize?: IconProps['size'];
};

export type BaseIconButtonElementProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	children?: never;
};

type BaseIconButtonProps = AsChildProps<BaseIconButtonElementProps> &
	BaseIconButtonDefaultProps &
	BaseIconButtonOwnProps;

export const BaseIconButton = forwardRef<HTMLButtonElement, BaseIconButtonProps>(
	(
		{
			asChild,
			analyticsId,
			itemDetails,
			itemValue,
			icon,
			iconSize = DEFAULT_SIZE,
			className,
			ariaLabel,
			children,
			padding,
			variant,
			disabled,
			...rest
		},
		ref,
	) => {
		return (
			<CircledIcon
				icon={icon}
				size={iconSize}
				className={className}
				variant={variant}
				padding={padding}
				disabled={disabled}>
				<ClickableAnalyticsSlot
					ref={ref}
					analyticsId={analyticsId}
					trackElementType={LegacySegmentTypes.ElementType.ELEMENT_TYPE_BUTTON}
					defaultElement="button"
					itemDetails={itemDetails}
					itemValue={itemValue}
					aria-label={ariaLabel}
					asChild={asChild}
					className={classNames(
						'absolute top-0 left-0 h-full w-full',
						disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					)}
					disabled={disabled}
					{...rest}>
					{children}
				</ClickableAnalyticsSlot>
			</CircledIcon>
		);
	},
);
